import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
  WhatsApp as WhatsAppIcon,
  ContentCopy as ContentCopyIcon,
  Lock as LockIcon,
  Launch as LaunchIcon,
  LinkOff as LinkOffIcon,
  PhonelinkLock as PhonelinkLockIcon,
  NoPhotography as NoPhotographyIcon,
  MyLocation as MyLocationIcon,
  PauseCircleOutline as PauseIcon,
  PlayCircleOutline as ResumeIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { getAdminControlPermissionRoutes } from '../../config/adminControlScreens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const PRODUCTION_URL = process.env.REACT_APP_PRODUCTION_URL || 'https://services.knls.in';

/**
 * Ensure every Admin Control Screens card appears in Edit Permissions.
 * API already merges these; this keeps UI labels in sync if hub config is ahead of backend.
 */
const mergeAdminControlScreensIntoGrouped = (grouped = {}) => {
  const next = {};
  Object.entries(grouped || {}).forEach(([category, routes]) => {
    next[category] = [...(routes || [])];
  });

  const byPermission = new Map();
  Object.entries(next).forEach(([category, routes]) => {
    routes.forEach((route, index) => {
      const key = typeof route === 'string' ? route : route.permission;
      if (key) byPermission.set(String(key).toLowerCase(), { category, index });
    });
  });

  getAdminControlPermissionRoutes().forEach((hubRoute) => {
    const key = String(hubRoute.permission).toLowerCase();
    const existing = byPermission.get(key);
    if (existing) {
      const list = next[existing.category];
      const prev = list[existing.index];
      list[existing.index] =
        typeof prev === 'string'
          ? { ...hubRoute, permission: prev }
          : {
              ...prev,
              name: hubRoute.name,
              description: hubRoute.description || prev.description,
              path: hubRoute.path || prev.path,
            };
      return;
    }
    if (!next[hubRoute.category]) next[hubRoute.category] = [];
    next[hubRoute.category].push(hubRoute);
    byPermission.set(key, {
      category: hubRoute.category,
      index: next[hubRoute.category].length - 1,
    });
  });
  return next;
};

/** Auto-selected when enabling Operator Call Leads — operators should re-login after permission changes */
const OPERATOR_LEADS_SCOPE_KEYS = [
  'operator_leads_scope_own',
  'operator_leads_scope_admin',
  'operator_leads_scope_team',
  'operator_leads_scope_unassigned',
];

/** Shown in My Operators create/edit; also listed under Bills in the full list (deduplicated). */
const OPERATOR_SECURITY_KEYS = {
  singleDevice: 'operator_security_single_device_binding',
  blockScreenshots: 'operator_security_block_screenshots',
};

const DEFAULT_OPERATOR_SECURITY_PERMISSIONS = [
  OPERATOR_SECURITY_KEYS.singleDevice,
  OPERATOR_SECURITY_KEYS.blockScreenshots,
];

const isOperatorSecurityPermission = (permission) =>
  permission === OPERATOR_SECURITY_KEYS.singleDevice ||
  permission === OPERATOR_SECURITY_KEYS.blockScreenshots;

/** Short labels for permission chips in the operators table */
const formatPermissionChipLabel = (permission) => {
  if (permission === OPERATOR_SECURITY_KEYS.singleDevice) return 'Single device';
  if (permission === OPERATOR_SECURITY_KEYS.blockScreenshots) return 'No screenshots';
  return permission.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const getAdminToken = () => localStorage.getItem('admin_token') || localStorage.getItem('token');
const getAuthHeaders = () => {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const validationSchema = yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Invalid phone number (10 digits)'),
  email: yup.string().email('Invalid email address'),
  full_name: yup.string().required('Full name is required'),
  password: yup
    .string()
    .required('Login PIN is required')
    .matches(/^\d{4,9}$/, 'PIN must be 4 to 9 digits')
    .test(
      'not-last4',
      'Do not use the last 4 digits of the phone as the PIN',
      function (value) {
        const phone = String(this.parent.phone || '').replace(/\D/g, '');
        const last4 = phone.slice(-4);
        return !(last4 && String(value) === last4);
      },
    ),
});

const getPinChangeSchema = (operatorPhone = '') =>
  yup.object({
    new_pin: yup
      .string()
      .required('New PIN is required')
      .matches(/^\d{4,9}$/, 'PIN must be 4 to 9 digits')
      .test(
        'not-last4',
        'Do not use the last 4 digits of the phone as the PIN',
        (value) => {
          const last4 = String(operatorPhone || '').replace(/\D/g, '').slice(-4);
          return !(last4 && String(value) === last4);
        },
      ),
    confirm_pin: yup
      .string()
      .required('Please confirm the PIN')
      .oneOf([yup.ref('new_pin')], 'PINs must match'),
  });

const MyOperator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [operators, setOperators] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [availableLmsCourses, setAvailableLmsCourses] = useState([]);
  const [lmsCourseSearch, setLmsCourseSearch] = useState('');
  const [routesByCategory, setRoutesByCategory] = useState({});
  const [allRoutes, setAllRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [createdOperator, setCreatedOperator] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedOperatorForPassword, setSelectedOperatorForPassword] = useState(null);
  const [hasLoginPin, setHasLoginPin] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [createPermissionSearch, setCreatePermissionSearch] = useState('');
  const [editPermissionSearch, setEditPermissionSearch] = useState('');
  const [resetDeviceBusyId, setResetDeviceBusyId] = useState(null);
  const [securityPermissionBusyId, setSecurityPermissionBusyId] = useState(null);
  const [locationBusyId, setLocationBusyId] = useState(null);
  const [radiusDialogOpen, setRadiusDialogOpen] = useState(false);
  const [radiusDialogOperator, setRadiusDialogOperator] = useState(null);
  const [radiusDraft, setRadiusDraft] = useState('500');
  const [operatorActionBusyId, setOperatorActionBusyId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load operators
      const operatorsResponse = await axios.get(
        `${API_BASE_URL}/app-admin-operators/list`,
        {
          headers: getAuthHeaders(),
        }
      );

      // Load available permissions (now includes route details)
      const permissionsResponse = await axios.get(
        `${API_BASE_URL}/app-admin-operators/available-permissions`,
        {
          headers: getAuthHeaders(),
        }
      );
      const lmsCoursesResponse = await axios.get(
        `${API_BASE_URL}/app-admin-operators/available-lms-courses`,
        { headers: getAuthHeaders() },
      );

      if (operatorsResponse.data.success) {
        setOperators(operatorsResponse.data.data || []);
      }

      if (permissionsResponse.data.success) {
        // Backward compatibility: support both old format (array) and new format (object with routes)
        const responseData = permissionsResponse.data;
        
        // Set simple permission list for backward compatibility
        if (Array.isArray(responseData.data)) {
          setAvailablePermissions(responseData.data);
        } else {
          setAvailablePermissions(responseData.data || []);
        }
        
        // Set new route structure if available
        if (responseData.routes && Array.isArray(responseData.routes)) {
          setAllRoutes(responseData.routes);
        }
        
        // Set grouped routes if available
        if (responseData.grouped) {
          setRoutesByCategory(
            mergeAdminControlScreensIntoGrouped(responseData.grouped),
          );
        } else if (responseData.routes && Array.isArray(responseData.routes)) {
          // Group routes by category if not already grouped
          const grouped = {};
          responseData.routes.forEach((route) => {
            const category = route.category || 'Other';
            if (!grouped[category]) {
              grouped[category] = [];
            }
            grouped[category].push(route);
          });
          setRoutesByCategory(mergeAdminControlScreensIntoGrouped(grouped));
        } else {
          setRoutesByCategory(mergeAdminControlScreensIntoGrouped({}));
        }

        // Keep simple list complete for fallback UI
        const hubPerms = getAdminControlPermissionRoutes().map((r) => r.permission);
        setAvailablePermissions((prev) => [
          ...new Set([...(prev || []), ...hubPerms]),
        ]);
      }
      if (lmsCoursesResponse.data?.success) {
        setAvailableLmsCourses(lmsCoursesResponse.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAppBaseUrl = () => {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host.includes('localhost') || host.includes('127.0.0.1')) {
        return window.location.origin;
      }
    }
    return PRODUCTION_URL;
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCreatePermissionSearch('');
    formik.resetForm();
  };

  const handleOpenPermissionDialog = (operator) => {
    setSelectedOperator(operator);
    // Get currently granted permissions
    const grantedPermissions = operator.permissions
      ?.filter((p) => p.is_granted)
      .map((p) => p.permission_name) || [];
    
    // Preserve existing permissions, new routes will default to unchecked
    setSelectedPermissions(grantedPermissions);
    setPermissionDialogOpen(true);
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setSelectedOperator(null);
    setSelectedPermissions([]);
    setEditPermissionSearch('');
    setLmsCourseSearch('');
  };

  const handleOpenPasswordDialog = async (operator) => {
    setSelectedOperatorForPassword(operator);
    setHasLoginPin(false);
    setPasswordDialogOpen(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/app-admin-operators/${operator.id}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.data.success && response.data.data.has_password) {
        setHasLoginPin(true);
      } else {
        setHasLoginPin(false);
      }
    } catch (err) {
      console.error('Error fetching operator details:', err);
      setHasLoginPin(true);
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setSelectedOperatorForPassword(null);
    setHasLoginPin(false);
  };

  const handleUpdatePassword = async (values) => {
    if (!selectedOperatorForPassword) return;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/app-admin-operators/${selectedOperatorForPassword.id}/password`,
        {
          new_pin: values.new_pin,
          confirm_pin: values.confirm_pin,
          // Compat with older API that still expects new_password
          new_password: values.new_pin,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.data.success) {
        setSuccess('Login PIN updated successfully');
        handleClosePasswordDialog();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to update login PIN');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update login PIN');
      console.error('Error updating login PIN:', err);
    }
  };

  const handleDirectLogin = async (operator) => {
    if (!operator?.id) return;

    setMagicLinkLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/magic-link`,
        {},
        { headers: getAuthHeaders() }
      );

      if (response.data.success && response.data.data?.token) {
        const baseUrl = getAppBaseUrl();
        const loginUrl = `${baseUrl}/app-admin-operator/login?token=${response.data.data.token}`;
        window.open(loginUrl, '_blank', 'noopener');
        setSuccess('Direct login link opened in a new tab');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to generate direct login link');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate direct login link');
      console.error('Error generating magic link:', err);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleResetDeviceBinding = async (operator) => {
    if (!operator?.id) return;
    if (
      !window.confirm(
        'Clear saved device for this operator? They can sign in on a new phone or browser after this (needed if they changed devices and single-device binding is enabled).'
      )
    ) {
      return;
    }
    setError('');
    setSuccess('');
    setResetDeviceBusyId(operator.id);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/reset-device-binding`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data?.success) {
        setSuccess(response.data.message || 'Device binding cleared.');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.data?.message || 'Failed to reset device binding');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset device binding');
      console.error('Reset device binding error:', err);
    } finally {
      setResetDeviceBusyId(null);
    }
  };

  const handleToggleOperatorStatus = async (operator) => {
    if (!operator?.id) return;
    const nextStatus = operator.status === 'active' ? 'inactive' : 'active';
    const action = nextStatus === 'active' ? 'resume' : 'pause';
    if (!window.confirm(`${action === 'pause' ? 'Pause' : 'Resume'} ${operator.full_name}?`)) {
      return;
    }

    setError('');
    setSuccess('');
    setOperatorActionBusyId(operator.id);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/status`,
        { status: nextStatus },
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setSuccess(response.data.message || `Operator ${action}d successfully`);
        await loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data?.message || `Failed to ${action} operator`);
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${action} operator`);
    } finally {
      setOperatorActionBusyId(null);
    }
  };

  const handleDeleteOperator = async (operator) => {
    if (!operator?.id) return;
    if (
      !window.confirm(
        `Delete ${operator.full_name}? This flags the account as deleted, blocks access, and removes it from this list.`,
      )
    ) {
      return;
    }

    setError('');
    setSuccess('');
    setOperatorActionBusyId(operator.id);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/app-admin-operators/${operator.id}`,
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setSuccess(response.data.message || 'Operator deleted');
        await loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data?.message || 'Failed to delete operator');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete operator');
    } finally {
      setOperatorActionBusyId(null);
    }
  };

  /** Raw granted permission keys as stored in DB (preserve casing for round-trip). */
  const getGrantedPermissionNamesForOperator = (operator) =>
    operator.permissions?.filter((p) => p.is_granted).map((p) => p.permission_name) || [];

  const permissionMatches = (a, b) =>
    String(a ?? '')
      .trim()
      .toLowerCase() === String(b ?? '').trim().toLowerCase();

  const handleToggleSecurityChip = async (operator, keyName) => {
    const perm =
      keyName === 'singleDevice'
        ? OPERATOR_SECURITY_KEYS.singleDevice
        : OPERATOR_SECURITY_KEYS.blockScreenshots;
    if (!operator?.id) return;
    setError('');
    setSecurityPermissionBusyId(operator.id);
    try {
      const current = getGrantedPermissionNamesForOperator(operator);
      const has = current.some((p) => permissionMatches(p, perm));
      const next = has
        ? current.filter((p) => !permissionMatches(p, perm))
        : [...current, perm];

      const response = await axios.put(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/permissions`,
        { permissions: next },
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setSuccess(
          'Security updated. Ask the operator to sign out and sign in if they are already logged in.',
        );
        setTimeout(() => setSuccess(''), 4500);
        await loadData();
      } else {
        setError(response.data?.message || 'Failed to update permissions');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 403
          ? 'Not allowed (need app admin).'
          : err.message);
      setError(msg || 'Failed to update permissions');
      console.error('Toggle security permission:', err);
    } finally {
      setSecurityPermissionBusyId(null);
    }
  };

  const clampRadiusMeters = (n) => {
    const x = parseInt(String(n), 10);
    if (Number.isNaN(x)) return 500;
    return Math.min(50000, Math.max(50, x));
  };

  const handleToggleLocationLock = async (operator) => {
    if (!operator?.id) return;
    setError('');
    setLocationBusyId(operator.id);
    try {
      const next = !Boolean(operator.location_lock_enabled);
      const response = await axios.put(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/location-lock`,
        { enabled: next },
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setSuccess('Location lock updated. Operator should refresh the app.');
        setTimeout(() => setSuccess(''), 4500);
        await loadData();
      } else {
        setError(response.data?.message || 'Failed to update location lock');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update location lock');
    } finally {
      setLocationBusyId(null);
    }
  };

  const handleSetRadius = async (operator, meters) => {
    if (!operator?.id) return;
    setError('');
    setLocationBusyId(operator.id);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/location-lock`,
        { radius_meters: clampRadiusMeters(meters) },
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setSuccess('Radius updated.');
        setTimeout(() => setSuccess(''), 3500);
        await loadData();
      } else {
        setError(response.data?.message || 'Failed to update radius');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update radius');
    } finally {
      setLocationBusyId(null);
    }
  };

  const handleResetOfficeLocation = async (operator) => {
    if (!operator?.id) return;
    if (!window.confirm('Clear locked office GPS for this operator? They must set location again in the app.')) {
      return;
    }
    setError('');
    setLocationBusyId(operator.id);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/app-admin-operators/${operator.id}/location-lock/reset-office`,
        {},
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setSuccess(response.data.message || 'Office location reset.');
        setTimeout(() => setSuccess(''), 4500);
        await loadData();
      } else {
        setError(response.data?.message || 'Failed to reset office location');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reset office location');
    } finally {
      setLocationBusyId(null);
    }
  };

  const openRadiusDialog = (operator) => {
    setRadiusDialogOperator(operator);
    setRadiusDraft(String(operator.location_radius_meters ?? 500));
    setRadiusDialogOpen(true);
  };

  const applyRadiusDialog = async () => {
    if (!radiusDialogOperator) return;
    await handleSetRadius(radiusDialogOperator, radiusDraft);
    setRadiusDialogOpen(false);
    setRadiusDialogOperator(null);
  };

  const handlePermissionToggle = (permission) => {
    if (selectedPermissions.includes(permission)) {
      let next = selectedPermissions.filter((p) => p !== permission);
      if (permission === 'operator_call_leads') {
        next = next.filter((p) => !OPERATOR_LEADS_SCOPE_KEYS.includes(p));
      }
      setSelectedPermissions(next);
    } else {
      let next = [...selectedPermissions, permission];
      if (permission === 'operator_call_leads') {
        OPERATOR_LEADS_SCOPE_KEYS.forEach((k) => {
          if (!next.includes(k)) next.push(k);
        });
      }
      setSelectedPermissions(next);
    }
  };

  const toggleLmsCourseScope = (courseCode) => {
    const permission = `lms_course_scope:${String(courseCode).toLowerCase()}`;
    setSelectedPermissions((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission],
    );
  };

  const toggleCreateSecurityPermission = (key, checked) => {
    const current = formik.values.permissions;
    if (checked) {
      if (!current.includes(key)) {
        formik.setFieldValue('permissions', [...current, key]);
      }
    } else {
      formik.setFieldValue(
        'permissions',
        current.filter((p) => p !== key),
      );
    }
  };

  const toggleEditSecurityPermission = (key, checked) => {
    if (checked) {
      if (!selectedPermissions.includes(key)) {
        setSelectedPermissions([...selectedPermissions, key]);
      }
    } else {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== key));
    }
  };

  const formatPermissionLabel = (permission) =>
    permission.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const getRoutePermission = (route) =>
    typeof route === 'string' ? route : route.permission;

  const getRouteLabel = (route) =>
    typeof route === 'string'
      ? formatPermissionLabel(route)
      : route.name || formatPermissionLabel(route.permission);

  const getRouteSearchText = (route) => {
    if (typeof route === 'string') {
      return `${route} ${formatPermissionLabel(route)}`.toLowerCase();
    }
    return `${route.name || ''} ${route.permission || ''} ${route.path || ''} ${route.description || ''}`.toLowerCase();
  };

  const filterGroupedRoutes = (groupedRoutes, query) => {
    const trimmedQuery = query.trim().toLowerCase();
    if (!trimmedQuery) {
      return groupedRoutes;
    }

    return Object.entries(groupedRoutes).reduce((acc, [category, routes]) => {
      const matchingRoutes = routes.filter((route) =>
        getRouteSearchText(route).includes(trimmedQuery),
      );
      if (matchingRoutes.length > 0) {
        acc[category] = matchingRoutes;
      }
      return acc;
    }, {});
  };

  const filterPermissionList = (permissions, query) => {
    const trimmedQuery = query.trim().toLowerCase();
    if (!trimmedQuery) {
      return permissions;
    }
    return permissions.filter((permission) =>
      `${permission} ${formatPermissionLabel(permission)}`
        .toLowerCase()
        .includes(trimmedQuery),
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedOperator) return;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/app-admin-operators/${selectedOperator.id}/permissions`,
        {
          permissions: selectedPermissions,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.data.success) {
        setSuccess('Permissions updated successfully');
        handleClosePermissionDialog();
        loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to update permissions');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update permissions');
      console.error('Error updating permissions:', err);
    }
  };

  const handleShareViaWhatsApp = (operator, password = null) => {
    const loginUrl = `${PRODUCTION_URL}/app-admin-operator/login`;
    const phoneNumber = operator.phone.replace('+91', '');
    
    let message = `Hello ${operator.full_name || 'Operator'},

Your LOCSY App Operator account credentials:

📱 Phone: ${phoneNumber}`;
    
    if (password) {
      message += `\n🔑 Login PIN: ${password}`;
    } else {
      message += `\n⚠️ Please contact admin for your login PIN.`;
    }
    
    message += `\n🔗 Login Link: ${loginUrl}

You can access:
- Job Alerts Management
- Pending Works
- Bills Creation
- Manage Bills

Thank you!`;

    // Open WhatsApp Web/App with the message (user can choose recipient)
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCopyToClipboard = (operator, password = null) => {
    const loginUrl = `${PRODUCTION_URL}/app-admin-operator/login`;
    const phoneNumber = operator.phone.replace('+91', '');
    
    let text = `LOCSY App Operator Login Details\n\n`;
    text += `Phone: ${phoneNumber}\n`;
    if (password) {
      text += `Login PIN: ${password}\n`;
    }
    text += `Login Link: ${loginUrl}`;
    
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleShareCreatedOperator = () => {
    if (!createdOperator) return;
    
    const loginUrl = `${PRODUCTION_URL}/app-admin-operator/login`;
    
    const message = `Hello ${createdOperator.full_name},

Your LOCSY App Operator account has been created successfully!

Login Details:
📱 Phone: ${createdOperator.phone.replace('+91', '')}
🔑 Login PIN: ${createdOperator.password}
🔗 Login Link: ${loginUrl}

You can now login and access:
- Job Alerts Management
- Pending Works
- Bills Creation
- Manage Bills

Thank you!`;

    // Open WhatsApp Web/App with the message (user can choose recipient)
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const formik = useFormik({
    initialValues: {
      phone: '',
      email: '',
      full_name: '',
      password: '',
      permissions: [...DEFAULT_OPERATOR_SECURITY_PERMISSIONS],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError('');
      setSuccess('');

      try {
        const response = await axios.post(
          `${API_BASE_URL}/app-admin-operators/create`,
          {
            phone: values.phone,
            email: values.email,
            full_name: values.full_name,
            password: values.password,
            permissions: values.permissions,
          },
          {
            headers: getAuthHeaders(),
          }
        );

        if (response.data.success) {
          setSuccess('Operator created successfully');
          // Store created operator details for sharing
          setCreatedOperator({
            phone: values.phone,
            password: values.password,
            full_name: values.full_name,
          });
          handleCloseDialog();
          formik.resetForm();
          loadData();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.data.message || 'Failed to create operator');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to create operator');
        console.error('Error creating operator:', err);
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      new_pin: '',
      confirm_pin: '',
    },
    validationSchema: getPinChangeSchema(selectedOperatorForPassword?.phone),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError('');
      await handleUpdatePassword(values);
      passwordFormik.resetForm();
    },
  });

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 3 } }}>
        <Box
          sx={{
            mb: { xs: 2.5, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} gutterBottom>
              My Operators
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage operators with access to job alerts, pending works, bills creation, and manage bills
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            Create Operator
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }} 
            onClose={() => {
              setSuccess('');
              setCreatedOperator(null);
            }}
            action={
              createdOperator && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyToClipboard(
                      { phone: createdOperator.phone, full_name: createdOperator.full_name },
                      createdOperator.password
                    )}
                    sx={{ color: 'primary.main' }}
                  >
                    Copy
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<WhatsAppIcon />}
                    onClick={handleShareCreatedOperator}
                    sx={{ color: '#25D366' }}
                  >
                    WhatsApp
                  </Button>
                </Box>
              )
            }
          >
            {success}
            {createdOperator && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Operator Created Successfully!
                </Typography>
                <Typography variant="body2">
                  📱 Phone: <strong>{createdOperator.phone.replace('+91', '')}</strong>
                </Typography>
                <Typography variant="body2">
                  🔑 Login PIN: <strong>{createdOperator.password}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  🔗 Login: {PRODUCTION_URL}/app-admin-operator/login
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {copySuccess && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCopySuccess(false)}>
            Copied to clipboard!
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <CardContent sx={{ p: { xs: 0, md: 2 }, '&:last-child': { pb: { xs: 0, md: 2 } } }}>
              <TableContainer sx={{ overflowX: { xs: 'visible', md: 'auto' } }}>
                <Table
                  sx={{
                    display: { xs: 'block', md: 'table' },
                    '& .MuiTableBody-root': { display: { xs: 'block', md: 'table-row-group' } },
                    '& .MuiTableRow-root': {
                      display: { xs: 'block', md: 'table-row' },
                      m: { xs: 1.25, md: 0 },
                      p: { xs: 1.25, md: 0 },
                      border: { xs: '1px solid', md: 0 },
                      borderColor: { xs: 'divider', md: 'transparent' },
                      borderRadius: { xs: 2, md: 0 },
                      boxShadow: { xs: 1, md: 0 },
                    },
                    '& .MuiTableCell-root': {
                      display: { xs: 'flex', md: 'table-cell' },
                      justifyContent: { xs: 'space-between', md: 'initial' },
                      gap: { xs: 2, md: 0 },
                      px: { xs: 0.5, md: 2 },
                      py: { xs: 1, md: 2 },
                      textAlign: { xs: 'right', md: 'left' },
                      overflowWrap: 'anywhere',
                      '&::before': {
                        content: { xs: 'attr(data-label)', md: 'none' },
                        fontWeight: 700,
                        color: 'text.secondary',
                        textAlign: 'left',
                        flexShrink: 0,
                      },
                      '&.stack-cell': {
                        flexDirection: { xs: 'column', md: 'initial' },
                        alignItems: { xs: 'stretch', md: 'initial' },
                        textAlign: 'left',
                      },
                    },
                  }}
                >
                  <TableHead sx={{ display: { xs: 'none', md: 'table-header-group' } }}>
                    <TableRow>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {operators.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No operators found. Create your first operator.
                        </TableCell>
                      </TableRow>
                    ) : (
                      operators.map((operator) => {
                        const grantedPermissions = operator.permissions
                          ?.filter((p) => p.is_granted)
                          .map((p) => p.permission_name) || [];
                        const grantedOtherPermissions = grantedPermissions.filter(
                          (p) =>
                            !permissionMatches(p, OPERATOR_SECURITY_KEYS.singleDevice) &&
                            !permissionMatches(p, OPERATOR_SECURITY_KEYS.blockScreenshots),
                        );
                        const hasDeviceLock = grantedPermissions.some((p) =>
                          permissionMatches(p, OPERATOR_SECURITY_KEYS.singleDevice),
                        );
                        const hasScreenshotBlock = grantedPermissions.some((p) =>
                          permissionMatches(p, OPERATOR_SECURITY_KEYS.blockScreenshots),
                        );

                        return (
                          <TableRow key={operator.id}>
                            <TableCell data-label="Phone">{operator.phone}</TableCell>
                            <TableCell data-label="Email">{operator.email || 'N/A'}</TableCell>
                            <TableCell data-label="Full Name">{operator.full_name}</TableCell>
                            <TableCell data-label="Status">
                              <Chip
                                label={
                                  operator.status === 'active'
                                    ? 'Active'
                                    : operator.status === 'inactive'
                                      ? 'Paused'
                                      : operator.status
                                }
                                color={operator.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell data-label="Permissions" className="stack-cell">
                              {grantedOtherPermissions.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {grantedOtherPermissions.map((permission) => (
                                    <Chip
                                      key={permission}
                                      label={formatPermissionChipLabel(permission)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              ) : grantedPermissions.length > 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  Security only — see Actions
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No permissions
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell data-label="Last Login">
                              {operator.last_login
                                ? new Date(operator.last_login).toLocaleString()
                                : 'Never'}
                            </TableCell>
                            <TableCell
                              data-label="Actions"
                              className="stack-cell"
                              sx={{ maxWidth: 420, verticalAlign: 'top' }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Security — green = enabled, red = disabled (click to toggle)
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    <Tooltip title="Restrict sign-in to one device per platform (Android / iOS / Web). Use Reset device after phone change. Green = ON, red = OFF.">
                                      <Box
                                        component="span"
                                        sx={{ display: 'inline-flex' }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          icon={<PhonelinkLockIcon sx={{ fontSize: 18 }} />}
                                          label="Device lock"
                                          color={hasDeviceLock ? 'success' : 'error'}
                                          variant={hasDeviceLock ? 'filled' : 'outlined'}
                                          onClick={() => handleToggleSecurityChip(operator, 'singleDevice')}
                                          disabled={securityPermissionBusyId === operator.id}
                                          sx={{ cursor: securityPermissionBusyId === operator.id ? 'wait' : 'pointer' }}
                                        />
                                      </Box>
                                    </Tooltip>
                                    <Tooltip title="Block screenshots on Android (FLAG_SECURE). Limited on web/iOS. Green = ON, red = OFF.">
                                      <Box
                                        component="span"
                                        sx={{ display: 'inline-flex' }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          icon={<NoPhotographyIcon sx={{ fontSize: 18 }} />}
                                          label="No screenshots"
                                          color={hasScreenshotBlock ? 'success' : 'error'}
                                          variant={hasScreenshotBlock ? 'filled' : 'outlined'}
                                          onClick={() => handleToggleSecurityChip(operator, 'blockScreenshots')}
                                          disabled={securityPermissionBusyId === operator.id}
                                          sx={{ cursor: securityPermissionBusyId === operator.id ? 'wait' : 'pointer' }}
                                        />
                                      </Box>
                                    </Tooltip>
                                    <Tooltip title="Geo-fence for the operator mobile app: must stay within radius of saved office GPS. Reset office after moving shop.">
                                      <Box
                                        component="span"
                                        sx={{ display: 'inline-flex' }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          icon={<MyLocationIcon sx={{ fontSize: 18 }} />}
                                          label="Location lock"
                                          color={operator.location_lock_enabled ? 'success' : 'error'}
                                          variant={operator.location_lock_enabled ? 'filled' : 'outlined'}
                                          onClick={() => handleToggleLocationLock(operator)}
                                          disabled={locationBusyId === operator.id}
                                          sx={{ cursor: locationBusyId === operator.id ? 'wait' : 'pointer' }}
                                        />
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Office radius (meters) — presets or custom (50–50,000m)
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                    {[500, 1000, 2000, 5000, 10000].map((m) => (
                                      <Chip
                                        key={m}
                                        size="small"
                                        label={m >= 1000 ? `${m / 1000} km` : `${m} m`}
                                        variant={
                                          Number(operator.location_radius_meters ?? 500) === m
                                            ? 'filled'
                                            : 'outlined'
                                        }
                                        color="primary"
                                        onClick={() => handleSetRadius(operator, m)}
                                        disabled={locationBusyId === operator.id}
                                        sx={{ cursor: locationBusyId === operator.id ? 'wait' : 'pointer' }}
                                      />
                                    ))}
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => openRadiusDialog(operator)}
                                      disabled={locationBusyId === operator.id}
                                    >
                                      Custom…
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      onClick={() => handleResetOfficeLocation(operator)}
                                      disabled={locationBusyId === operator.id}
                                    >
                                      Reset office GPS
                                    </Button>
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    width: '100%',
                                    '& .MuiButton-root': {
                                      flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
                                    },
                                  }}
                                >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleOpenPermissionDialog(operator)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color={operator.status === 'active' ? 'warning' : 'success'}
                                  startIcon={
                                    operator.status === 'active' ? <PauseIcon /> : <ResumeIcon />
                                  }
                                  onClick={() => handleToggleOperatorStatus(operator)}
                                  disabled={operatorActionBusyId === operator.id}
                                >
                                  {operator.status === 'active' ? 'Pause' : 'Resume'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<LockIcon />}
                                  onClick={() => handleOpenPasswordDialog(operator)}
                                >
                                  Update PIN
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={<LinkOffIcon />}
                                  onClick={() => handleResetDeviceBinding(operator)}
                                  disabled={resetDeviceBusyId === operator.id}
                                >
                                  Reset device
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<LaunchIcon />}
                                  onClick={() => handleDirectLogin(operator)}
                                  disabled={magicLinkLoading || operator.status !== 'active'}
                                >
                                  Direct Login
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<WhatsAppIcon />}
                                  onClick={() => handleShareViaWhatsApp(operator)}
                                  sx={{ 
                                    color: '#25D366',
                                    borderColor: '#25D366',
                                    '&:hover': {
                                      borderColor: '#25D366',
                                      backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    }
                                  }}
                                >
                                  WhatsApp
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => handleCopyToClipboard(operator)}
                                >
                                  Copy Link
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleDeleteOperator(operator)}
                                  disabled={operatorActionBusyId === operator.id}
                                >
                                  Delete
                                </Button>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Create Operator Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Create New Operator</DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                    placeholder="10 digit phone number"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email (Optional)"
                    name="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="full_name"
                    value={formik.values.full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                    helperText={formik.touched.full_name && formik.errors.full_name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Login PIN"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={
                      (formik.touched.password && formik.errors.password) ||
                      '4–9 digits (same policy as LOCSY Dashboard). Do not use last 4 of phone.'
                    }
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 9,
                      autoComplete: 'new-password',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      App security (LOCSY Dashboard — enabled by default)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      Single-device binding and screenshot blocking apply when operators use the mobile app. Turn off only if you intentionally trust the device.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.permissions.includes(OPERATOR_SECURITY_KEYS.singleDevice)}
                          onChange={(e) =>
                            toggleCreateSecurityPermission(
                              OPERATOR_SECURITY_KEYS.singleDevice,
                              e.target.checked,
                            )}
                          size="small"
                        />
                      }
                      label="Single device per platform (admin can use Reset device if they change phones)"
                      sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.permissions.includes(OPERATOR_SECURITY_KEYS.blockScreenshots)}
                          onChange={(e) =>
                            toggleCreateSecurityPermission(
                              OPERATOR_SECURITY_KEYS.blockScreenshots,
                              e.target.checked,
                            )}
                          size="small"
                        />
                      }
                      label="Block screenshots & screen capture (Android FLAG_SECURE; limited on web/iOS)"
                      sx={{ display: 'flex', alignItems: 'flex-start' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Initial Permissions (can be changed later):
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search permissions, route name, or path..."
                    value={createPermissionSearch}
                    onChange={(e) => setCreatePermissionSearch(e.target.value)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    {Object.keys(routesByCategory).length > 0 ? (
                      // New format: Grouped by category
                      Object.entries(filterGroupedRoutes(routesByCategory, createPermissionSearch)).map(([category, routes]) => (
                        <Box key={category} sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                            {category}
                          </Typography>
                          {routes
                            .filter((route) => !isOperatorSecurityPermission(getRoutePermission(route)))
                            .map((route) => {
                            const permission = getRoutePermission(route);
                            return (
                              <FormControlLabel
                                key={permission}
                                control={
                                  <Checkbox
                                    checked={formik.values.permissions.includes(permission)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        formik.setFieldValue('permissions', [
                                          ...formik.values.permissions,
                                          permission,
                                        ]);
                                      } else {
                                        formik.setFieldValue(
                                          'permissions',
                                          formik.values.permissions.filter((p) => p !== permission)
                                        );
                                      }
                                    }}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {getRouteLabel(route)}
                                  </Typography>
                                }
                                sx={{ display: 'block', mb: 0.5 }}
                              />
                            );
                          })}
                        </Box>
                      ))
                    ) : (
                      // Fallback: Simple list format
                      filterPermissionList(
                        availablePermissions.filter((p) => !isOperatorSecurityPermission(p)),
                        createPermissionSearch,
                      ).map((permission) => (
                        <FormControlLabel
                          key={permission}
                          control={
                            <Checkbox
                              checked={formik.values.permissions.includes(permission)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  formik.setFieldValue('permissions', [
                                    ...formik.values.permissions,
                                    permission,
                                  ]);
                                } else {
                                  formik.setFieldValue(
                                    'permissions',
                                    formik.values.permissions.filter((p) => p !== permission)
                                  );
                                }
                              }}
                              size="small"
                            />
                          }
                          label={formatPermissionLabel(permission)}
                          sx={{ display: 'block', mb: 0.5 }}
                        />
                      ))
                    )}
                    {Object.keys(routesByCategory).length > 0 &&
                      Object.keys(filterGroupedRoutes(routesByCategory, createPermissionSearch)).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No permissions found for this search.
                        </Typography>
                      )}
                    {Object.keys(routesByCategory).length === 0 &&
                      filterPermissionList(availablePermissions, createPermissionSearch).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No permissions found for this search.
                        </Typography>
                      )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                Create
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Edit Permissions Dialog */}
        <Dialog
          open={permissionDialogOpen}
          onClose={handleClosePermissionDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            Edit Permissions - {selectedOperator?.full_name}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the permissions to grant to this operator. Includes all dashboard screens plus every card on{' '}
              <strong>Admin Control Screens</strong> (on/off). New hub cards appear here automatically (default unchecked).
              Operators must log out and log in after changes.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Operator Call Leads — Lead queue scope:</strong> When you turn on &quot;Operator Call Leads&quot;, we also select the four
              &quot;Lead queue — …&quot; options (your bills, admin bills, team operators, legacy rows). Uncheck any to narrow the list.
              If none of those four are saved, the API treats visibility as <strong>full team</strong> (backward compatible). Operators must <strong>log out and log in</strong> for changes to apply.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                App security (LOCSY Dashboard)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Turn on for standard protection. Use <strong>Reset device</strong> in the table if this operator changes phones with single-device enabled.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedPermissions.includes(OPERATOR_SECURITY_KEYS.singleDevice)}
                    onChange={(e) =>
                      toggleEditSecurityPermission(OPERATOR_SECURITY_KEYS.singleDevice, e.target.checked)
                    }
                    size="small"
                    color="primary"
                  />
                }
                label="Single device per platform"
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedPermissions.includes(OPERATOR_SECURITY_KEYS.blockScreenshots)}
                    onChange={(e) =>
                      toggleEditSecurityPermission(OPERATOR_SECURITY_KEYS.blockScreenshots, e.target.checked)
                    }
                    size="small"
                    color="primary"
                  />
                }
                label="Block screenshots & screen capture (Android)"
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              />
            </Paper>
            {selectedPermissions.includes('lms_builder') && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.light' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  LMS Builder course access
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Select one or more courses to restrict this operator. Leave every course unchecked for access to all LMS Builder courses (existing behavior).
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={lmsCourseSearch}
                  onChange={(event) => setLmsCourseSearch(event.target.value)}
                  placeholder="Search courses by name or code..."
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {availableLmsCourses
                  .filter((course) => {
                    const query = lmsCourseSearch.trim().toLowerCase();
                    return !query || `${course.course_name || ''} ${course.course_code || ''}`.toLowerCase().includes(query);
                  })
                  .map((course) => {
                  const permission = `lms_course_scope:${String(course.course_code).toLowerCase()}`;
                  return (
                    <FormControlLabel
                      key={course.course_code}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedPermissions.includes(permission)}
                          onChange={() => toggleLmsCourseScope(course.course_code)}
                        />
                      }
                      label={`${course.course_name || course.course_code} (${course.course_code})`}
                      sx={{ display: 'flex', alignItems: 'flex-start' }}
                    />
                  );
                })}
                {!availableLmsCourses.length && (
                  <Typography variant="caption" color="text.secondary">No platform LMS courses are available.</Typography>
                )}
                {availableLmsCourses.length > 0 &&
                  !availableLmsCourses.some((course) => {
                    const query = lmsCourseSearch.trim().toLowerCase();
                    return !query || `${course.course_name || ''} ${course.course_code || ''}`.toLowerCase().includes(query);
                  }) && (
                    <Typography variant="caption" color="text.secondary">No courses match your search.</Typography>
                  )}
              </Paper>
            )}
            <TextField
              fullWidth
              size="small"
              placeholder="Search permissions, route name, or path..."
              value={editPermissionSearch}
              onChange={(e) => setEditPermissionSearch(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              {/* Use grouped routes if available, otherwise fall back to simple list */}
              {Object.keys(routesByCategory).length > 0 ? (
              // New format: Grouped by category
              Object.entries(filterGroupedRoutes(routesByCategory, editPermissionSearch)).map(([category, routes]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight={600} 
                    sx={{ mb: 1.5, color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}
                  >
                    {category}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {routes
                      .filter((route) => !isOperatorSecurityPermission(getRoutePermission(route)))
                      .map((route) => {
                      const permission = getRoutePermission(route);
                      const isChecked = selectedPermissions.includes(permission);
                      return (
                        <Grid item xs={12} key={permission}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handlePermissionToggle(permission)}
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {getRouteLabel(route)}
                                </Typography>
                                {typeof route === 'object' && route.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                    {route.description}
                                  </Typography>
                                )}
                                {typeof route === 'object' && route.path && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                    {route.path}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ 
                              alignItems: 'flex-start',
                              '& .MuiFormControlLabel-label': {
                                flex: 1,
                              }
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))
            ) : (
              // Fallback: Simple list format (backward compatibility)
              <Grid container spacing={2}>
                {filterPermissionList(
                  availablePermissions.filter((p) => !isOperatorSecurityPermission(p)),
                  editPermissionSearch,
                ).map((permission) => (
                  <Grid item xs={12} key={permission}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedPermissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                          color="primary"
                        />
                      }
                      label={formatPermissionLabel(permission)}
                    />
                  </Grid>
                ))}
              </Grid>
              )}
              {Object.keys(routesByCategory).length > 0 &&
                Object.keys(filterGroupedRoutes(routesByCategory, editPermissionSearch)).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No permissions found for this search.
                  </Typography>
                )}
              {Object.keys(routesByCategory).length === 0 &&
                filterPermissionList(availablePermissions, editPermissionSearch).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No permissions found for this search.
                  </Typography>
                )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClosePermissionDialog}>Cancel</Button>
            <Button onClick={handleSavePermissions} variant="contained">
              Save Permissions
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Login PIN Dialog */}
        <Dialog
          open={passwordDialogOpen}
          onClose={handleClosePasswordDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            Update PIN - {selectedOperatorForPassword?.full_name}
          </DialogTitle>
          <form onSubmit={passwordFormik.handleSubmit}>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set a 4–9 digit login PIN (same policy as{' '}
                <a href="https://loksy.in/locsydashboard" target="_blank" rel="noreferrer">
                  LOCSY Dashboard
                </a>
                ). Do not use the last 4 digits of the phone number.
                {hasLoginPin
                  ? ' A PIN is already set; entering a new one will replace it.'
                  : ' No PIN is set yet for this operator.'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New PIN"
                    name="new_pin"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordFormik.values.new_pin}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.new_pin && Boolean(passwordFormik.errors.new_pin)}
                    helperText={
                      (passwordFormik.touched.new_pin && passwordFormik.errors.new_pin) ||
                      '4–9 digits'
                    }
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 9,
                      autoComplete: 'new-password',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm PIN"
                    name="confirm_pin"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordFormik.values.confirm_pin}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.confirm_pin && Boolean(passwordFormik.errors.confirm_pin)}
                    helperText={passwordFormik.touched.confirm_pin && passwordFormik.errors.confirm_pin}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 9,
                      autoComplete: 'new-password',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePasswordDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                Update PIN
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog open={radiusDialogOpen} onClose={() => setRadiusDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Custom office radius</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              label="Radius (meters)"
              type="number"
              value={radiusDraft}
              onChange={(e) => setRadiusDraft(e.target.value)}
              inputProps={{ min: 50, max: 50000 }}
              helperText="Allowed range: 50 to 50,000 meters."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRadiusDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyRadiusDialog}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default MyOperator;

